import { useAuthUser } from '@/hooks/useAuthUser';
import { firebaseAuth } from '@/lib/Firebase';
import { useQueryClient } from '@tanstack/react-query';
import React, { createContext, useCallback, useContext, useRef, useState } from 'react';

interface LoginModalContextValue {
  isOpen: boolean;
  requireAuth: () => Promise<boolean>;
  closeModal: () => void;
  onLoginSuccess: () => void;
  isAuthenticated: boolean;
}

const LoginModalContext = createContext<LoginModalContextValue | null>(null);

export function LoginModalProvider({ children }: { children: React.ReactNode }) {
  const { data: authUser } = useAuthUser(['authUser'], firebaseAuth);
  const [isOpen, setIsOpen] = useState(false);
  const resolveRef = useRef<((value: boolean) => void) | null>(null);
  const queryClient = useQueryClient();
  

  const requireAuth = useCallback((): Promise<boolean> => {
    if (!!authUser) {
      return Promise.resolve(true);
    }

    setIsOpen(true);
    return new Promise<boolean>((resolve) => {
      resolveRef.current = resolve;
    });
  }, [authUser]);

  const closeModal = useCallback(() => {
    setIsOpen(false);
    resolveRef.current?.(false);
    resolveRef.current = null;
  }, []);

  const onLoginSuccess = useCallback(() => {
    const current = firebaseAuth.currentUser;
    if (current) {
      queryClient.setQueryData(['authUser'], current);
    } else {
      void queryClient.invalidateQueries({ queryKey: ['authUser'] });
    }
    void queryClient.invalidateQueries({ queryKey: ['user'] });
    setIsOpen(false);
    resolveRef.current?.(true);
    resolveRef.current = null;
  }, [queryClient]);

  return (
    <LoginModalContext.Provider
      value={{
        isOpen,
        requireAuth,
        closeModal,
        onLoginSuccess,
        isAuthenticated: !!authUser,
      }}
    >
      {children}
    </LoginModalContext.Provider>
  );
}

export function useLoginModal() {
  const context = useContext(LoginModalContext);
  if (!context) {
    throw new Error('useLoginModal must be used within a LoginModalProvider');
  }
  return context;
}
